import { useState, useEffect, useCallback } from "react";

const FAVORITES_KEY = "freedailypro-favorites";
const RECENT_KEY = "freedailypro-recent-tools";
const MAX_RECENT = 10;

interface RecentEntry {
  toolId: string;
  visitedAt: number;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

/** Favorites: a simple starred list, entirely local to this browser. */
export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    setFavorites(readJson(FAVORITES_KEY, []));
  }, []);

  const toggleFavorite = useCallback((toolId: string) => {
    setFavorites(prev => {
      const next = prev.includes(toolId) ? prev.filter(id => id !== toolId) : [...prev, toolId];
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const isFavorite = useCallback((toolId: string) => favorites.includes(toolId), [favorites]);

  return { favorites, toggleFavorite, isFavorite };
}

/** Recently used tools, most-recent-first, capped at MAX_RECENT. Call
 * recordVisit() once when a tool page loads (see ToolPage.tsx). */
export function useRecentTools() {
  const [recent, setRecent] = useState<RecentEntry[]>([]);

  useEffect(() => {
    setRecent(readJson(RECENT_KEY, []));
  }, []);

  const recordVisit = useCallback((toolId: string) => {
    setRecent(prev => {
      const withoutThis = prev.filter(e => e.toolId !== toolId);
      const next = [{ toolId, visitedAt: Date.now() }, ...withoutThis].slice(0, MAX_RECENT);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  return { recent: recent.map(e => e.toolId), recordVisit };
}
